
export type PaymentMode = "RAZORPAY" | "STRIPE" | "WALLET" | "PARTNER_HOSTED" | "PARTNER_TOKENISED";

export type WalletStatus = "HELD" | "TOP_UP_REQUIRED" | "SPLIT_REQUIRED";

export type RazorpayCheckout = "BOTH" | "EMBEDDED" | "HOSTED";

export type Gateway = "RAZORPAY" | "STRIPE";

export interface iRazorpayBlock {
  checkout?: RazorpayCheckout;
  orderId?: string;
  keyId?: string;
  amount?: number;
  razorpayAmount?: number;
  currency?: string;
  paymentLinkId?: string;
  shortUrl?: string;
}

export interface iWalletLeg {
  gateway?: Gateway;
  id?: string;
  amount?: number;
  currency?: string;
  razorpay?: iRazorpayBlock;
}

export interface iWalletPayment {
  status?: WalletStatus;
  required?: number;
  held?: number;
  shortfall?: number;
  currency?: string;
  spendable?: number;
  topUp?: iWalletLeg;
  split?: iWalletLeg;
  topUpSessionId?: string;
  splitSessionId?: string;
}

// The engine prices a fee per payment method (today 2% on IN card bookings, none on UPI or a
// wallet). `baseAmount` is the price-review total, `chargeAmount` what the gateway collects.
export interface iConvenienceFee {
  paymentMethod?: string;
  percent?: number;
  fee?: number;
  baseAmount?: number;
  chargeAmount?: number;
  countryIso?: string | null;
  productType?: string;
}

export interface iCheckoutResponse {
  paymentMode?: PaymentMode | string;
  paymentProvider?: string;
  gatewayPaymentId?: string;
  paymentLink?: string | null;
  paymentIntentId?: string | null;
  clientSecret?: string | null;
  razorpay?: iRazorpayBlock;
  walletPayment?: iWalletPayment;
  chargeCurrency?: string;
  convenienceFee?: iConvenienceFee;
}

export type EmbeddedHandle =
  | {
      provider: "RAZORPAY";
      orderId: string;
      keyId: string;
      amountMinor: number;
      currency: string;
    }
  | {
      provider: "STRIPE";
      paymentIntentId: string;
      clientSecret: string;
    };

export interface CheckoutDoors {
  hosted: boolean;
  embedded: boolean;
}

export interface CheckoutPlan {
  mode: PaymentMode;
  gateway: Gateway | PaymentMode | null;
  collect: number;
  alreadyCovered: number;
  total: number;
  currency: string;
  hosted: string;
  embedded: EmbeddedHandle | null;
  doors: CheckoutDoors;
  walletStatus: WalletStatus | null;
  isTopUp: boolean;
  isSplit: boolean;
  then: "confirm";
}

const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const str = (v: unknown): string => (typeof v === "string" ? v : "");

const razorpayDoors = (rzp: iRazorpayBlock | undefined, hostedLink: string): CheckoutDoors => {
  const checkout = rzp?.checkout ?? "BOTH";
  const hasEmbedded = !!(rzp?.orderId && rzp?.keyId);
  return {
    hosted: checkout !== "EMBEDDED" && !!hostedLink,
    embedded: checkout !== "HOSTED" && hasEmbedded,
  };
};

const razorpayHandle = (rzp: iRazorpayBlock | undefined, fallbackCurrency: string): EmbeddedHandle | null => {
  if (!rzp?.orderId || !rzp?.keyId) return null;
  return {
    provider: "RAZORPAY",
    orderId: rzp.orderId,
    keyId: rzp.keyId,
    amountMinor: num(rzp.razorpayAmount),
    currency: str(rzp.currency) || fallbackCurrency,
  };
};

const stripeHandle = (res: iCheckoutResponse): EmbeddedHandle | null => {
  const id = str(res.paymentIntentId);
  const secret = str(res.clientSecret);
  if (!id || !secret) return null;
  return { provider: "STRIPE", paymentIntentId: id, clientSecret: secret };
};

export const howToPay = (res: iCheckoutResponse | null | undefined): CheckoutPlan => {
  const r: iCheckoutResponse = res ?? {};
  const mode = (str(r.paymentMode) || "STRIPE") as PaymentMode;
  const hosted = str(r.paymentLink);
  const w = r.walletPayment;

  if (w && w.status === "HELD") {
    return {
      mode: "WALLET",
      gateway: null,
      collect: 0,
      alreadyCovered: num(w.required),
      total: num(w.required),
      currency: str(w.currency),
      hosted: "",
      embedded: null,
      doors: { hosted: false, embedded: false },
      walletStatus: "HELD",
      isTopUp: false,
      isSplit: false,
      then: "confirm",
    };
  }

  if (w && (w.status === "TOP_UP_REQUIRED" || w.status === "SPLIT_REQUIRED")) {
    const isSplit = w.status === "SPLIT_REQUIRED";
    const leg: iWalletLeg = (isSplit ? w.split : w.topUp) ?? {};
    const currency = str(leg.currency) || str(w.currency);
    const rzp = leg.razorpay ?? r.razorpay;
    const gateway: Gateway = leg.gateway === "STRIPE" ? "STRIPE" : "RAZORPAY";

    const embedded = gateway === "RAZORPAY" ? razorpayHandle(rzp, currency) : stripeHandle(r);
    const doors =
      gateway === "RAZORPAY"
        ? razorpayDoors(rzp, hosted)
        : { hosted: !!hosted, embedded: !!embedded };

    return {
      mode: "WALLET",
      gateway,
      collect: num(w.shortfall),
      alreadyCovered: num(w.held),
      total: num(w.required),
      currency,
      hosted: doors.hosted ? hosted : "",
      embedded: doors.embedded ? embedded : null,
      doors,
      walletStatus: w.status,
      isTopUp: !isSplit,
      isSplit,
      then: "confirm",
    };
  }

  if (mode === "RAZORPAY") {
    const rzp = r.razorpay;
    const currency = str(rzp?.currency);
    const doors = razorpayDoors(rzp, hosted);
    const embedded = razorpayHandle(rzp, currency);
    return {
      mode: "RAZORPAY",
      gateway: "RAZORPAY",
      collect: num(rzp?.amount),
      alreadyCovered: 0,
      total: num(rzp?.amount),
      currency,
      hosted: doors.hosted ? hosted : "",
      embedded: doors.embedded ? embedded : null,
      doors,
      walletStatus: null,
      isTopUp: false,
      isSplit: false,
      then: "confirm",
    };
  }

  if (mode === "PARTNER_HOSTED" || mode === "PARTNER_TOKENISED") {
    return {
      mode,
      gateway: mode,
      collect: 0,
      alreadyCovered: 0,
      total: 0,
      currency: "",
      hosted,
      embedded: null,
      doors: { hosted: !!hosted, embedded: false },
      walletStatus: null,
      isTopUp: false,
      isSplit: false,
      then: "confirm",
    };
  }

  const embedded = stripeHandle(r);
  return {
    mode: "STRIPE",
    gateway: "STRIPE",
    collect: 0,
    alreadyCovered: 0,
    total: 0,
    currency: "",
    hosted,
    embedded,
    doors: { hosted: !!hosted, embedded: !!embedded },
    walletStatus: null,
    isTopUp: false,
    isSplit: false,
    then: "confirm",
  };
};

export type ConfirmHintField = "paymentId" | "paymentIntentId";

export interface ConfirmHint {
  field: ConfirmHintField;
  value: string;
}

export const confirmHintFor = (plan: CheckoutPlan, paidId: string | null | undefined): ConfirmHint | null => {
  const value = str(paidId);
  if (!value) return null;

  if (plan.mode === "STRIPE" || plan.gateway === "STRIPE") {
    if (value.startsWith("cs_")) return { field: "paymentId", value };
    if (value.startsWith("pi_")) return { field: "paymentIntentId", value };
    return { field: "paymentId", value };
  }

  return { field: "paymentId", value };
};

export const pickCheckoutFields = (response: any): iCheckoutResponse => ({
  paymentMode: response?.paymentMode,
  paymentProvider: response?.paymentProvider,
  gatewayPaymentId: response?.gatewayPaymentId,
  paymentLink: response?.paymentLink,
  paymentIntentId: response?.paymentIntentId,
  clientSecret: response?.clientSecret,
  razorpay: response?.razorpay,
  walletPayment: response?.walletPayment,
  chargeCurrency: response?.chargeCurrency,
  convenienceFee: response?.convenienceFee,
});

export const confirmHintForId = (paidId: string | null | undefined): ConfirmHint | null => {
  const value = str(paidId);
  if (!value) return null;
  if (value.startsWith("pi_")) return { field: "paymentIntentId", value };
  return { field: "paymentId", value };
};

export const isNothingToCollect = (plan: CheckoutPlan): boolean =>
  plan.collect <= 0 && !plan.doors.hosted && !plan.doors.embedded;

export const logCheckoutPlan = (
  where: string,
  plan: CheckoutPlan,
  extra?: Record<string, unknown>
): void => {
  /* eslint-disable no-console */
  console.log(`[checkout-handoff] ${where}`, {
    mode: plan.mode,
    gateway: plan.gateway,
    walletStatus: plan.walletStatus,
    collect: plan.collect,
    alreadyCovered: plan.alreadyCovered,
    total: plan.total,
    currency: plan.currency,
    doors: `hosted=${plan.doors.hosted} embedded=${plan.doors.embedded}`,
    embedded:
      plan.embedded?.provider === "RAZORPAY"
        ? `RAZORPAY ${plan.embedded.orderId} ${plan.embedded.amountMinor} (minor units)`
        : plan.embedded?.provider === "STRIPE"
          ? `STRIPE ${plan.embedded.paymentIntentId}`
          : null,
    hosted: plan.hosted || null,
    ...extra,
  });
  /* eslint-enable no-console */
};

export const logCheckoutRaw = (where: string, res: iCheckoutResponse | null | undefined): void => {
  /* eslint-disable no-console */
  console.log(`[checkout-handoff] ${where} · raw`, {
    paymentMode: res?.paymentMode ?? "(absent)",
    paymentProvider: res?.paymentProvider ?? "(absent)",
    gatewayPaymentId: res?.gatewayPaymentId ?? "(absent)",
    paymentLink: res?.paymentLink || null,
    paymentIntentId: res?.paymentIntentId || null,
    hasClientSecret: !!res?.clientSecret,
    razorpay: res?.razorpay
      ? {
          checkout: res.razorpay.checkout,
          orderId: res.razorpay.orderId,
          amount: res.razorpay.amount,
          razorpayAmount: res.razorpay.razorpayAmount,
          currency: res.razorpay.currency,
        }
      : null,
    walletPayment: res?.walletPayment
      ? {
          status: res.walletPayment.status,
          required: res.walletPayment.required,
          held: res.walletPayment.held,
          shortfall: res.walletPayment.shortfall,
          currency: res.walletPayment.currency,
          leg: res.walletPayment.split ? "split" : res.walletPayment.topUp ? "topUp" : null,
          legGateway: (res.walletPayment.split ?? res.walletPayment.topUp)?.gateway ?? null,
          legHasRazorpay: !!(res.walletPayment.split ?? res.walletPayment.topUp)?.razorpay,
        }
      : null,
  });
  /* eslint-enable no-console */
};
